import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Button, Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import { DocumentUploadDrawer } from "@/components/documents/DocumentUploadDrawer";
import { DocumentDetailDrawer } from "@/components/documents/DocumentDetailDrawer";
import type { DocumentRow, DocRowStatus, DocumentType } from "@/api/types";

const DOT_COLOR: Record<DocRowStatus, string> = {
  current: "#16a34a",
  expiring: "#eab308",
  expired: "#dc2626",
  in_progress: "#d1d5db",
  missing: "#dc2626",
};

const STATUS_LABEL: Record<DocRowStatus, string> = {
  current: "Current",
  expiring: "Expiring",
  expired: "Expired",
  in_progress: "In progress",
  missing: "Missing",
};

interface Props {
  rows: DocumentRow[];
  investigatorId: string;
}

export function InvestigatorDocumentsTable({ rows, investigatorId }: Props) {
  const { id: siteId, da_code: daCode } = useParams<{
    id: string;
    da_code?: string;
  }>();
  const [uploadFor, setUploadFor] = useState<DocumentType | null>(null);
  const [viewDocId, setViewDocId] = useState<string | null>(null);

  return (
    <>
      {/* Fixed table-layout + per-column widths keep the column edges
          steady when switching between investigator tabs — the actions
          column would otherwise resize between "Upload" and
          "View / replace" widths and shift everything else. */}
      <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell>Document</TableCell>
            <TableCell sx={{ width: 160 }}>Status</TableCell>
            <TableCell sx={{ width: 140 }}>Expiration</TableCell>
            <TableCell sx={{ width: 150 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.doc_type}>
              <TableCell>{r.label}</TableCell>
              <TableCell>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{
                    width: 9, height: 9, borderRadius: "50%",
                    bgcolor: DOT_COLOR[r.status],
                  }} />
                  {STATUS_LABEL[r.status]}
                </Box>
              </TableCell>
              <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                {r.expires_at ?? "—"}
              </TableCell>
              <TableCell align="right">
                {r.document_id ? (
                  <Button
                    size="small"
                    onClick={() => setViewDocId(r.document_id)}
                  >
                    View / replace
                  </Button>
                ) : (
                  <Button
                    size="small"
                    onClick={() => setUploadFor(r.doc_type as DocumentType)}
                  >
                    Upload
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DocumentUploadDrawer
        open={uploadFor !== null}
        onClose={() => setUploadFor(null)}
        entityType="investigator"
        entityId={investigatorId}
        documentType={uploadFor ?? undefined}
        invalidateKeys={[
          ["sites", siteId, "clinops", daCode],
          ["documents"],
        ]}
      />
      <DocumentDetailDrawer
        open={viewDocId !== null}
        onClose={() => setViewDocId(null)}
        documentId={viewDocId}
        invalidateKeys={[
          ["sites", siteId, "clinops", daCode],
          ["documents"],
        ]}
      />
    </>
  );
}
